import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import moment from "moment-timezone";
import { MessageCircleQuestionMark } from "lucide-react";
import { Label } from "../../../../../../components/ui/label";
import Dialog from "../../../../../../components/shared/Dialog";
import ResourceSelect from "../../../../../../components/shared/inputs/ResourceSelect";
import Button from "../../../../../../components/shared/Button";
import ResponseTable from "./ResponseTable";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import useApi from "../../../../../../utils/api";
import { useCompanyDateFormat } from "../../../../../../utils/hooks/company";
import { formatTimeSheetParams } from "../../../../../TimeSheet/api/helpers";
import { firstToUpper } from "../../../../../../utils/helpers/string";
import { getErrorMessage } from "../../../../../../utils/helpers/errors";
import { withLoadingToast } from "../../../helpers/withLoadingToast";

function SaveDayTemplate({ visible, close, date }: any) {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const [allFilters] = useFilters();
    const [data, setData] = useState([]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [template, setTemplate] = useState<any>();
    const { authPost } = useApi();

    const cancel = useCallback(() => {
        setData([]);
        setIsSubmitted(false);
        close();
    }, [close]);

    const saveDayAsTemplate = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: (data: any) => authPost(`/shift-templates/v2/${template?.id}/bulk`, { data }),
        onSuccess: (response) => {
            setData(response);
            setIsSubmitted(true);
        },
        onError: (err) => getErrorMessage(err, t),
    });

    const save = useCallback(() => {
        const filters = {
            pageIndex: Number(allFilters?.page) - 1,
            pageSize: allFilters?.pageSize,
            ...formatTimeSheetParams({
                filters: allFilters?.filters,
                from: moment(date).format("YYYY-MM-DD"),
                to: moment(date).format("YYYY-MM-DD")
            }),
            sort: allFilters?.sort
        };
        if (allFilters?.mode === "month") {
            delete filters.pageIndex;
            delete filters.pageSize;
        }
        saveDayAsTemplate.mutate({ filters });
    }, [allFilters, date, saveDayAsTemplate]);

    useEffect(() => {
        if (!visible) setTemplate(undefined);
    }, [visible]);

    return (
        <Dialog
            open={visible}
            close={cancel}
            title={(
                <div className="tw-flex tw-gap-3 tw-items-baseline">
                    <h1 className="tw-m-0">{t("save-as-template")}</h1>
                    <p className="tw-m-0 tw-text-sm tw-text-neutral-500">{"("}{moment(date).format(dateFormat)}{")"}</p>
                </div>
            )}
            size="2xl"
            bodyClassName="tw-max-h-[60vh] tw-overflow-auto"
            footer={(
                <div className="tw-flex tw-justify-end tw-my-2 tw-gap-2">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={cancel}
                        disabled={!template?.id || saveDayAsTemplate.isPending}
                        loading={saveDayAsTemplate.isPending}
                    >
                        {t("cancel")}
                    </Button>
                    {!isSubmitted && (
                        <Button
                            type="button"
                            disabled={!template?.id || saveDayAsTemplate.isPending}
                            loading={saveDayAsTemplate.isPending}
                            onClick={save}
                        >
                            {t("save")}
                        </Button>
                    )}
                </div>
            )}
        >
            {isSubmitted ? (
                <ResponseTable data={data} />
            ) : (
                <div className="tw-flex tw-flex-col tw-gap-7 tw-items-center tw-justify-center tw-mb-7">
                    <MessageCircleQuestionMark size={50} className="tw-text-primary" />
                    <h1 className="tw-text-center">{t("do-you-want-to-save-day-events-as-templates")}</h1>
                    <div className="tw-w-full">
                        <Label className="tw-mb-2">
                            {firstToUpper(t("select-template"))}
                        </Label>
                        <ResourceSelect
                            resourcePath="/schedule-templates"
                            mode="single"
                            placeholder={firstToUpper(t("template"))}
                            labelPropName="name"
                            value={template}
                            onChange={(value: any) => setTemplate(value)}
                        />
                    </div>
                </div>
            )}
        </Dialog>
    );
}

export default SaveDayTemplate;
